/**
 * 換算設定の編集モードの状態と、その組み立て・バリデーション（画面の部品から切り離して試せるようにする）。
 * 行の一覧はテーブルごとに持つ（店舗×SKUの単価は、基本クレジットと累進テーブルの2つ）。
 */

import type {
  ConsumptionItemOption,
  SaveEntryRequest,
  TableRows,
  Violation,
} from '@/lib/api/credit-conversion-settings';
import type { SettingItem, TableDef } from '@/lib/credit-conversion-settings/tables';
import {
  checkDate,
  checkNumber,
  checkRows,
  duplicatedRows,
  type RowInput,
} from '@/lib/credit-conversion-settings/validation';

export type EditorRow = RowInput;

/** 1つのテーブルの行の一覧 */
export interface EditorPart {
  table: TableDef;
  rows: EditorRow[];
}

/** 適用開始日の入力欄のキー */
export const DATE_FIELD = 'effective_dt';

/** 行の入力欄のキー（行のキーは `/` を含まない） */
export function cellKey(rowKey: string, column: string): string {
  return `${rowKey}/${column}`;
}

export function parseCellKey(key: string): { rowKey: string; column: string } {
  const slash = key.indexOf('/');
  return { rowKey: key.slice(0, slash), column: key.slice(slash + 1) };
}

export interface EditorErrors {
  /** 入力欄のエラー（キーは DATE_FIELD か cellKey） */
  fields: Record<string, string>;
  /** 行のエラー（軸の重複）。行のキーはどのテーブルの行でも重ならない */
  rows: Record<string, string>;
  /** 行にも入力欄にも属さないエラー（キーはテーブルの物理名） */
  tables: Record<string, string[]>;
}

export const noErrors: EditorErrors = { fields: {}, rows: {}, tables: {} };

export function hasErrors(errors: EditorErrors): boolean {
  return (
    Object.keys(errors.fields).length > 0 ||
    Object.keys(errors.rows).length > 0 ||
    Object.values(errors.tables).some((messages) => messages.length > 0)
  );
}

let sequence = 0;
export function newRowKey(): string {
  sequence += 1;
  return `row-${sequence}`;
}

function emptyRow(table: TableDef): EditorRow {
  return {
    key: newRowKey(),
    values: Object.fromEntries(table.columns.map((c) => [c.name, ''])),
  };
}

/**
 * 入力欄の初期値の行。消費項目別の係数では、初期値にない消費項目の行を係数を空欄にして足し、序数の順に並べる。
 * 1つの適用開始日に1行を持つテーブルで初期値に行がないとき（店舗×SKUの単価で、2つのテーブルの適用開始日が
 * そろっていないとき）は、空欄の行1つを足す。そのテーブルでは行を追加できないため。
 */
export function initialRows(
  table: TableDef,
  rows: Record<string, string>[],
  consumptionItems: ConsumptionItemOption[],
): EditorRow[] {
  const copied = rows.map((values) => ({ key: newRowKey(), values: { ...values } }));
  if (table.name === 'consumption_item_coefficients') {
    const known = consumptionItems.map((item) => {
      const existing = copied.find((row) => row.values.consumption_item === item.value);
      return (
        existing ?? { key: newRowKey(), values: { consumption_item: item.value, coefficient: '' } }
      );
    });
    // 列挙型にない消費項目の行は、残してサーバーのバリデーションに任せる
    const unknown = copied.filter(
      (row) => !consumptionItems.some((item) => item.value === row.values.consumption_item),
    );
    return [...known, ...unknown];
  }
  if (!table.hasAxis && copied.length === 0) return [emptyRow(table)];
  return copied;
}

/** 取得した値から、テーブルごとの入力欄の初期値を作る */
export function initialParts(
  item: SettingItem,
  tables: TableRows[],
  consumptionItems: ConsumptionItemOption[],
): EditorPart[] {
  return item.tables.map((table) => ({
    table,
    rows: initialRows(
      table,
      tables.find((t) => t.table === table.name)?.rows ?? [],
      consumptionItems,
    ),
  }));
}

/** 設定項目に値がないときの「追加」の初期値。テーブルごとに決める */
export function defaultParts(
  item: SettingItem,
  consumptionItems: ConsumptionItemOption[],
): EditorPart[] {
  return item.tables.map((table) => ({
    table,
    rows:
      table.name === 'store_sku_progressive_rates' ? [] : initialRows(table, [], consumptionItems),
  }));
}

export function addedRow(table: TableDef): EditorRow {
  return emptyRow(table);
}

/** 入力の変更を判定するための写し（画面が足した行は、写しを取る前に足しておく） */
export function snapshotOf(date: string, parts: EditorPart[]): string {
  return JSON.stringify([date, parts.map((part) => part.rows.map((row) => row.values))]);
}

/** 保存で送る行 */
export function requestTables(parts: EditorPart[]): SaveEntryRequest['tables'] {
  return Object.fromEntries(parts.map((part) => [part.table.name, part.rows.map((r) => r.values)]));
}

/** その行がある行の一覧 */
export function partOfRow(parts: EditorPart[], rowKey: string): EditorPart | undefined {
  return parts.find((part) => part.rows.some((row) => row.key === rowKey));
}

/** 入力欄の単項目チェック */
export function checkField(
  parts: EditorPart[],
  key: string,
  value: string,
  today: string,
): string | null {
  if (key === DATE_FIELD) return checkDate(value, today);
  const { rowKey, column } = parseCellKey(key);
  const def = partOfRow(parts, rowKey)?.table.columns.find((c) => c.name === column);
  return def?.kind === 'number' ? checkNumber(value) : null;
}

/** 「保存」の押下時のチェック。単項目チェックでエラーがなければ、テーブルごとに行とテーブル全体のチェックを行う */
export function validateForSave(parts: EditorPart[], date: string, today: string): EditorErrors {
  const fields: Record<string, string> = {};
  const dateError = checkDate(date, today);
  if (dateError) fields[DATE_FIELD] = dateError;
  for (const { table, rows } of parts) {
    for (const row of rows) {
      for (const column of table.columns) {
        if (column.kind !== 'number') continue;
        const error = checkNumber(row.values[column.name] ?? '');
        if (error) fields[cellKey(row.key, column.name)] = error;
      }
    }
  }
  if (Object.keys(fields).length > 0) return { ...noErrors, fields };
  const errors: EditorErrors = { fields: {}, rows: {}, tables: {} };
  for (const { table, rows } of parts) {
    const { rowErrors, tableErrors } = checkRows(table, rows);
    Object.assign(errors.rows, rowErrors);
    if (tableErrors.length > 0) errors.tables[table.name] = tableErrors;
  }
  return errors;
}

/** 軸の値が変わったとき（行を削除したときも）、重複のエラーがある行のうち重複でなくなった行のエラーを消す */
export function clearResolvedDuplicates(
  parts: EditorPart[],
  rowErrors: Record<string, string>,
): Record<string, string> {
  const duplicated = new Set(parts.flatMap((part) => [...duplicatedRows(part.table, part.rows)]));
  return Object.fromEntries(
    Object.entries(rowErrors).filter(([key]) => duplicated.has(key) && !!partOfRow(parts, key)),
  );
}

/** サーバーのバリデーションのエラーを、表示先ごとに振り分ける。行の番号は、そのテーブルの送った行の並び */
export function errorsFromViolations(parts: EditorPart[], violations: Violation[]): EditorErrors {
  const errors: EditorErrors = { fields: {}, rows: {}, tables: {} };
  for (const v of violations) {
    if (v.field === DATE_FIELD) {
      errors.fields[DATE_FIELD] = v.message;
      continue;
    }
    const part = parts.find((p) => p.table.name === v.table) ?? parts[0];
    const row = v.row === null ? undefined : part.rows[v.row];
    if (row && v.column) {
      errors.fields[cellKey(row.key, v.column)] = v.message;
    } else if (row) {
      errors.rows[row.key] = v.message;
    } else {
      errors.tables[part.table.name] = [...(errors.tables[part.table.name] ?? []), v.message];
    }
  }
  return errors;
}
